// src/components/admin/VendorAdminPanel.tsx
// DROP 18b — Admin panel to manage vendor categories and applications

import React, { useState, useEffect, useCallback } from "react";
import {
  Settings, Users, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, RefreshCw, Plus, Edit2,
  Trash2, Eye, Award, Download, Filter, Search,
  ToggleLeft, ToggleRight, AlertTriangle
} from "lucide-react";

interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
  fee: number;
  currency: string;
  maxSlots: number;
  filledSlots: number;
  isOpen: boolean;
  criteria: string[];
  requiredDocs: string[];
}

interface Application {
  id: string;
  categoryId: string;
  categoryName: string;
  fullName: string;
  businessName: string;
  phone: string;
  email: string;
  state: string;
  experience: string;
  description: string;
  idDocUrl?: string;
  portfolioUrl?: string;
  status: "pending_payment"|"pending_review"|"approved"|"rejected";
  paymentStatus: "pending"|"confirmed"|"failed";
  paystackRef?: string;
  amountPaid?: number;
  accreditationId?: string;
  appliedAt: string;
  reviewedAt?: string;
  reviewNote?: string;
}

type TabType = "categories" | "applications";
type AppFilter = "all" | "pending_review" | "approved" | "rejected";

const STATUS_STYLES: Record<string, { bg:string; text:string; border:string; label:string }> = {
  pending_payment: { bg:"rgba(250,204,21,0.1)", text:"#FDE047", border:"rgba(250,204,21,0.3)", label:"Awaiting Payment" },
  pending_review:  { bg:"rgba(59,130,246,0.1)", text:"#93C5FD", border:"rgba(59,130,246,0.3)", label:"Under Review" },
  approved:        { bg:"rgba(74,222,128,0.1)", text:"#4ADE80", border:"rgba(74,222,128,0.3)", label:"Approved" },
  rejected:        { bg:"rgba(239,68,68,0.1)",  text:"#FCA5A5", border:"rgba(239,68,68,0.3)",  label:"Rejected" },
};

export default function VendorAdminPanel() {
  const [tab,          setTab]          = useState<TabType>("applications");
  const [categories,   setCategories]   = useState<Category[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [filter,       setFilter]       = useState<AppFilter>("pending_review");
  const [search,       setSearch]       = useState("");
  const [expanded,     setExpanded]     = useState<string|null>(null);
  const [editCat,      setEditCat]      = useState<Category|null>(null);
  const [showNewCat,   setShowNewCat]   = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [msg,          setMsg]          = useState<{type:"ok"|"err";text:string}|null>(null);
  const [reviewNote,   setReviewNote]   = useState("");

  const token = () => localStorage.getItem("erue_auth_token") || "";
  const flash = (type:"ok"|"err", text:string) => {
    setMsg({type,text}); setTimeout(()=>setMsg(null),4000);
  };
  const api = useCallback(async (url:string, opts:RequestInit={}) => {
    const res = await fetch(url, { ...opts, headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}`, ...(opts.headers||{}) }});
    return res.json();
  }, []);

  const loadCategories = useCallback(async () => {
    const d = await api("/api/vendor/admin/categories");
    if (d.success) setCategories(d.categories || []);
  }, [api]);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api("/api/vendor/admin/applications");
      if (d.success) setApplications(d.applications || []);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadCategories(); loadApplications(); }, [loadCategories, loadApplications]);

  const toggleCatOpen = async (cat: Category) => {
    const d = await api(`/api/vendor/admin/categories/${cat.id}`, {
      method:"PUT", body:JSON.stringify({ isOpen: !cat.isOpen })
    });
    if (d.success) { flash("ok",`Category ${!cat.isOpen?"opened":"closed"}`); loadCategories(); }
    else flash("err", d.error||"Failed");
  };

  const saveCat = async (cat: Partial<Category> & { id?: string }) => {
    const url    = cat.id ? `/api/vendor/admin/categories/${cat.id}` : "/api/vendor/admin/categories";
    const method = cat.id ? "PUT" : "POST";
    const d = await api(url, { method, body:JSON.stringify(cat) });
    if (d.success) {
      flash("ok", cat.id ? "Category updated" : "Category created");
      setEditCat(null); setShowNewCat(false); loadCategories();
    } else flash("err", d.error||"Failed");
  };

  const deleteCat = async (id: string) => {
    if (!confirm("Delete this category?")) return;
    const d = await api(`/api/vendor/admin/categories/${id}`, { method:"DELETE" });
    if (d.success) { flash("ok","Category deleted"); loadCategories(); }
    else flash("err", d.error||"Failed");
  };

  const reviewApp = async (app: Application, action: "approve"|"reject") => {
    const d = await api(`/api/vendor/admin/applications/${app.id}/review`, {
      method:"POST",
      body:JSON.stringify({ action, note: reviewNote })
    });
    if (d.success) {
      flash("ok", action==="approve" ? `✅ ${app.fullName} APPROVED — Accreditation ID: ${d.accreditationId}` : `Application rejected`);
      setExpanded(null); setReviewNote(""); loadApplications();
    } else flash("err", d.error||"Failed");
  };

  const filtered = applications
    .filter(a => filter==="all" ? true : a.status===filter)
    .filter(a => !search || a.fullName.toLowerCase().includes(search.toLowerCase()) ||
                            a.email.toLowerCase().includes(search.toLowerCase()) ||
                            a.categoryName.toLowerCase().includes(search.toLowerCase()));

  const counts = {
    all:            applications.length,
    pending_review: applications.filter(a=>a.status==="pending_review").length,
    approved:       applications.filter(a=>a.status==="approved").length,
    rejected:       applications.filter(a=>a.status==="rejected").length,
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {([["applications","Applications",counts.all],["categories","Categories",categories.length]] as const).map(([t,label,count]) => (
          <button key={t} onClick={()=>setTab(t)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ background:tab===t?"#C9A84C":"transparent", color:tab===t?"#070D0A":"rgba(237,232,220,0.5)", border:`1px solid ${tab===t?"#C9A84C":"#1C2E22"}` }}>
            {label}
            <span className="text-xs px-1.5 py-0.5 rounded-full"
                  style={{ background:tab===t?"rgba(7,13,10,0.3)":"rgba(201,168,76,0.15)", color:tab===t?"#070D0A":"#C9A84C" }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Flash */}
      {msg && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
             style={{ background:msg.type==="ok"?"rgba(74,222,128,0.1)":"rgba(239,68,68,0.1)", border:`1px solid ${msg.type==="ok"?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)"}`, color:msg.type==="ok"?"#4ADE80":"#FCA5A5" }}>
          {msg.type==="ok"?<CheckCircle size={14}/>:<AlertTriangle size={14}/>} {msg.text}
        </div>
      )}

      {/* ── APPLICATIONS TAB ── */}
      {tab==="applications" && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(["all","pending_review","approved","rejected"] as AppFilter[]).map(f => (
              <button key={f} onClick={()=>setFilter(f)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all border"
                      style={{ background:filter===f?"#C9A84C":"transparent", borderColor:filter===f?"#C9A84C":"#1C2E22", color:filter===f?"#070D0A":"rgba(237,232,220,0.5)" }}>
                {f==="all"?"All":f==="pending_review"?"Under Review":f.charAt(0).toUpperCase()+f.slice(1)}
                <span className="ml-1.5 opacity-70">({counts[f]})</span>
              </button>
            ))}
            <div className="relative ml-auto">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:"rgba(237,232,220,0.3)" }}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
                     className="pl-8 pr-3 py-1.5 rounded-lg text-xs outline-none"
                     style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC", width:"180px" }}/>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
              <RefreshCw size={24} className="animate-spin mx-auto mb-2"/>Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
              <Users size={32} className="mx-auto mb-2 opacity-30"/>
              No applications {filter!=="all"?`with status "${filter}"`:""}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(app => {
                const st = STATUS_STYLES[app.status] || STATUS_STYLES.pending_review;
                const isOpen = expanded === app.id;
                return (
                  <div key={app.id} className="rounded-xl border overflow-hidden"
                       style={{ borderColor: isOpen?"rgba(201,168,76,0.3)":"#1C2E22", background:"#121E16" }}>
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
                             style={{ background:"rgba(15,81,50,0.5)", color:"#C9A84C" }}>
                          {app.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm" style={{ color:"#EDE8DC" }}>{app.fullName}</span>
                            {app.accreditationId && (
                              <span className="text-xs font-mono px-2 py-0.5 rounded"
                                    style={{ background:"rgba(74,222,128,0.1)", color:"#4ADE80" }}>
                                {app.accreditationId}
                              </span>
                            )}
                          </div>
                          <span className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
                            {app.categoryName} · {app.state}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded-full border font-medium"
                              style={{ background:st.bg, color:st.text, borderColor:st.border }}>
                          {st.label}
                        </span>
                        <button onClick={()=>setExpanded(isOpen?null:app.id)}
                                style={{ color:"rgba(237,232,220,0.4)" }}>
                          {isOpen?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="border-t p-4 space-y-4" style={{ borderColor:"#1C2E22" }}>
                        {/* Details grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[
                            ["Phone",       app.phone],
                            ["Email",       app.email],
                            ["LGA",         app.state],
                            ["Experience",  app.experience?.replace(/_/g," ")||"—"],
                            ["Payment",     app.paymentStatus],
                            ["Amount Paid", app.amountPaid ? `₦${app.amountPaid.toLocaleString()}` : "—"],
                            ["Applied",     new Date(app.appliedAt).toLocaleDateString()],
                            ["Paystack Ref", app.paystackRef || "—"],
                          ].map(([label, val]) => (
                            <div key={label}>
                              <p className="text-xs mb-0.5" style={{ color:"rgba(237,232,220,0.4)" }}>{label}</p>
                              <p className="text-sm" style={{ color:"#EDE8DC" }}>{val}</p>
                            </div>
                          ))}
                        </div>

                        {/* Description */}
                        <div>
                          <p className="text-xs mb-1" style={{ color:"rgba(237,232,220,0.4)" }}>Service Description</p>
                          <p className="text-sm" style={{ color:"rgba(237,232,220,0.7)" }}>{app.description}</p>
                        </div>

                        {/* Docs */}
                        {(app.idDocUrl || app.portfolioUrl) && (
                          <div className="flex gap-3">
                            {app.idDocUrl && (
                              <a href={app.idDocUrl} target="_blank" rel="noopener noreferrer"
                                 className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border"
                                 style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.6)" }}>
                                <Eye size={12}/> View ID
                              </a>
                            )}
                            {app.portfolioUrl && (
                              <a href={app.portfolioUrl} target="_blank" rel="noopener noreferrer"
                                 className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border"
                                 style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.6)" }}>
                                <Eye size={12}/> View Portfolio
                              </a>
                            )}
                          </div>
                        )}

                        {/* Existing review note */}
                        {app.reviewNote && (
                          <div className="p-3 rounded-lg" style={{ background:"rgba(237,232,220,0.05)", border:"1px solid #1C2E22" }}>
                            <p className="text-xs mb-1" style={{ color:"rgba(237,232,220,0.4)" }}>Review Note</p>
                            <p className="text-sm" style={{ color:"rgba(237,232,220,0.7)" }}>{app.reviewNote}</p>
                          </div>
                        )}

                        {/* Review actions — only for pending_review */}
                        {app.status === "pending_review" && (
                          <div>
                            <textarea value={reviewNote} onChange={e=>setReviewNote(e.target.value)}
                                      placeholder="Review note (optional — visible on approval/rejection)"
                                      rows={2} className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none mb-3"
                                      style={{ background:"#0E1812", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                            <div className="flex gap-2">
                              <button onClick={()=>reviewApp(app,"approve")}
                                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                                      style={{ background:"rgba(74,222,128,0.15)", color:"#4ADE80", border:"1px solid rgba(74,222,128,0.3)" }}>
                                <CheckCircle size={13}/> Approve & Accredit
                              </button>
                              <button onClick={()=>reviewApp(app,"reject")}
                                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                                      style={{ background:"rgba(239,68,68,0.1)", color:"#FCA5A5", border:"1px solid rgba(239,68,68,0.3)" }}>
                                <XCircle size={13}/> Reject
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── CATEGORIES TAB ── */}
      {tab==="categories" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
              Manage registration categories, fees and criteria
            </p>
            <button onClick={()=>setShowNewCat(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                    style={{ background:"#C9A84C", color:"#070D0A" }}>
              <Plus size={13}/> Add Category
            </button>
          </div>

          <div className="space-y-3">
            {categories.map(cat => (
              <div key={cat.id} className="p-4 rounded-xl border"
                   style={{ borderColor:"#1C2E22", background:"#121E16" }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{cat.icon}</span>
                    <div>
                      <span className="font-medium text-sm" style={{ color:"#EDE8DC" }}>{cat.name}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-bold" style={{ color:"#C9A84C" }}>
                          ₦{cat.fee.toLocaleString()}
                        </span>
                        <span className="text-xs" style={{ color:"rgba(237,232,220,0.3)" }}>
                          · {cat.filledSlots}/{cat.maxSlots||"∞"} slots
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={()=>toggleCatOpen(cat)} className="transition-colors"
                            style={{ color: cat.isOpen?"#4ADE80":"rgba(237,232,220,0.3)" }}>
                      {cat.isOpen ? <ToggleRight size={22}/> : <ToggleLeft size={22}/>}
                    </button>
                    <button onClick={()=>setEditCat(cat)} style={{ color:"#C9A84C" }}>
                      <Edit2 size={14}/>
                    </button>
                    <button onClick={()=>deleteCat(cat.id)} style={{ color:"rgba(239,68,68,0.6)" }}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
                <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>{cat.description}</p>
                {cat.criteria.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs mb-1" style={{ color:"rgba(201,168,76,0.6)" }}>Criteria:</p>
                    <ul className="space-y-0.5">
                      {cat.criteria.map((c,i)=>(
                        <li key={i} className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>• {c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Category form modal */}
          {(editCat || showNewCat) && (
            <CategoryForm
              initial={editCat}
              onSave={saveCat}
              onCancel={()=>{ setEditCat(null); setShowNewCat(false); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Category form component ────────────────────────────────────────
function CategoryForm({
  initial, onSave, onCancel
}: {
  initial: Category|null;
  onSave: (c: Partial<Category> & { id?:string }) => void;
  onCancel: () => void;
}) {
  const [name,     setName]     = useState(initial?.name     || "");
  const [icon,     setIcon]     = useState(initial?.icon     || "🏪");
  const [desc,     setDesc]     = useState(initial?.description || "");
  const [fee,      setFee]      = useState(String(initial?.fee || ""));
  const [currency, setCurrency] = useState(initial?.currency || "NGN");
  const [maxSlots, setMaxSlots] = useState(String(initial?.maxSlots || "0"));
  const [isOpen,   setIsOpen]   = useState(initial?.isOpen ?? true);
  const [criteria, setCriteria] = useState<string[]>(initial?.criteria || [""]);
  const [reqDocs,  setReqDocs]  = useState<string[]>(initial?.requiredDocs || [""]);

  const addCriteria = () => setCriteria(p => [...p,""]);
  const updCriteria = (i:number, v:string) => setCriteria(p => p.map((x,j)=>j===i?v:x));
  const delCriteria = (i:number) => setCriteria(p => p.filter((_,j)=>j!==i));

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      id: initial?.id,
      name: name.trim(), icon, description: desc.trim(),
      fee: Number(fee) || 0, currency, maxSlots: Number(maxSlots)||0,
      isOpen, criteria: criteria.filter(Boolean),
      requiredDocs: reqDocs.filter(Boolean),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0E1812] border border-[#C9A84C]/30 rounded-2xl p-6 max-h-[85vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-4" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
          {initial ? "Edit Category" : "New Category"}
        </h3>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-1">
              <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Icon</label>
              <input value={icon} onChange={e=>setIcon(e.target.value)}
                     className="w-full px-3 py-2.5 rounded-lg text-lg text-center outline-none"
                     style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
            </div>
            <div className="col-span-3">
              <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Category Name *</label>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. DJ / Music"
                     className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                     style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
            </div>
          </div>
          <div>
            <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Description</label>
            <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={2}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                      style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Fee *</label>
              <input value={fee} onChange={e=>setFee(e.target.value)} type="number" placeholder="0"
                     className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                     style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Currency</label>
              <select value={currency} onChange={e=>setCurrency(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                      style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}>
                <option value="NGN">NGN (₦)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Max Slots (0=∞)</label>
              <input value={maxSlots} onChange={e=>setMaxSlots(e.target.value)} type="number"
                     className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                     style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isOpen} onChange={e=>setIsOpen(e.target.checked)} className="accent-yellow-400"/>
            <span className="text-sm" style={{ color:"rgba(237,232,220,0.7)" }}>Registration Open</span>
          </label>

          {/* Criteria */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>Prequalification Criteria</label>
              <button onClick={addCriteria} className="text-xs" style={{ color:"#C9A84C" }}>+ Add</button>
            </div>
            {criteria.map((c,i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={c} onChange={e=>updCriteria(i,e.target.value)}
                       placeholder={`Criterion ${i+1}`}
                       className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                <button onClick={()=>delCriteria(i)} style={{ color:"rgba(239,68,68,0.6)" }}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg text-sm border"
                  style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>Cancel</button>
          <button onClick={handleSave} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background:"#C9A84C", color:"#070D0A" }}>
            {initial ? "Update Category" : "Create Category"}
          </button>
        </div>
      </div>
    </div>
  );
}
